import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from './ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const { toast } = useToast();

  const emptyPost = {
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    categories: [],
    tags: [],
    seo_title: '',
    seo_description: '',
    published: true
  };

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts?published_only=false`);
      setPosts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: "Error",
        description: "Failed to fetch blog posts",
        variant: "destructive"
      });
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCreate = () => {
    setCurrentPost(emptyPost);
    setEditMode(true);
  };

  const handleEdit = (post) => {
    setCurrentPost(post);
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      if (!currentPost.title || !currentPost.slug || !currentPost.content) {
        toast({
          title: "Validation Error",
          description: "Title, slug, and content are required",
          variant: "destructive"
        });
        return;
      }

      if (currentPost.id) {
        // Update existing post
        await axios.put(`${API_URL}/api/blog/posts/${currentPost.slug}`, currentPost);
        toast({
          title: "Success",
          description: "Post updated successfully"
        });
      } else {
        // Create new post
        await axios.post(`${API_URL}/api/blog/posts`, currentPost);
        toast({
          title: "Success",
          description: "Post created successfully"
        });
      }

      setEditMode(false);
      setCurrentPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save post",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API_URL}/api/blog/posts/${slug}`);
      toast({
        title: "Success",
        description: "Post deleted successfully"
      });
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold-500 text-xl">Loading...</div>
      </div>
    );
  }

  if (editMode) {
    return (
      <div className="min-h-screen bg-black text-white pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="font-display text-4xl font-bold text-gold-500">
              {currentPost.id ? 'Edit Post' : 'Create New Post'}
            </h1>
            <div className="flex gap-3">
              <Button 
                onClick={handleSave}
                className="bg-gold-500 hover:bg-gold-600 text-black"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Post
              </Button>
              <Button 
                onClick={() => {
                  setEditMode(false);
                  setCurrentPost(null);
                }}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <Input
                value={currentPost.title}
                onChange={(e) => {
                  setCurrentPost({
                    ...currentPost,
                    title: e.target.value,
                    slug: generateSlug(e.target.value)
                  });
                }}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter post title"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Slug * (URL-friendly)
              </label>
              <Input
                value={currentPost.slug}
                onChange={(e) => setCurrentPost({...currentPost, slug: generateSlug(e.target.value)})}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="post-url-slug"
              />
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Excerpt
              </label>
              <Textarea
                value={currentPost.excerpt}
                onChange={(e) => setCurrentPost({...currentPost, excerpt: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white min-h-[100px]"
                placeholder="Short description of the post"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Content * (Markdown supported)
              </label>
              <Textarea
                value={currentPost.content}
                onChange={(e) => setCurrentPost({...currentPost, content: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white min-h-[400px] font-mono text-sm"
                placeholder="Write your content here... You can use markdown formatting."
              />
            </div>

            {/* Featured Image */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Featured Image URL
              </label>
              <Input
                value={currentPost.featured_image}
                onChange={(e) => setCurrentPost({...currentPost, featured_image: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="https://example.com/image.jpg"
              />
              {currentPost.featured_image && (
                <img 
                  src={currentPost.featured_image} 
                  alt="Preview" 
                  className="mt-3 w-full h-48 object-cover rounded-lg"
                />
              )}
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Categories (comma-separated)
              </label>
              <Input
                value={currentPost.categories.join(', ')}
                onChange={(e) => setCurrentPost({
                  ...currentPost, 
                  categories: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Social Media, Marketing, SEO"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags (comma-separated)
              </label>
              <Input
                value={currentPost.tags.join(', ')}
                onChange={(e) => setCurrentPost({
                  ...currentPost, 
                  tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="instagram, growth, 2025"
              />
            </div>

            {/* SEO Title */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SEO Title
              </label>
              <Input
                value={currentPost.seo_title}
                onChange={(e) => setCurrentPost({...currentPost, seo_title: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="SEO-optimized title (60 chars max)"
              />
            </div>

            {/* SEO Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SEO Description
              </label>
              <Textarea
                value={currentPost.seo_description}
                onChange={(e) => setCurrentPost({...currentPost, seo_description: e.target.value})}
                className="bg-gray-900 border-gray-700 text-white min-h-[80px]"
                placeholder="SEO meta description (160 chars max)"
              />
            </div>

            {/* Published */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={currentPost.published}
                onChange={(e) => setCurrentPost({...currentPost, published: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm font-medium text-gray-300">
                Published (visible to public)
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-display text-4xl font-bold text-gold-500">Blog Admin</h1>
          <Button 
            onClick={handleCreate}
            className="bg-gold-500 hover:bg-gold-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>

        {posts.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            No posts yet. Create your first post!
          </div>
        ) : (
          <div className="grid gap-6">
            {posts.map((post) => (
              <Card key={post.id} className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-xl text-white">{post.title}</CardTitle>
                        {!post.published && (
                          <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                            Draft
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 mb-3">
                        /{post.slug}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {post.categories.map((cat) => (
                          <Badge key={cat} className="bg-gold-500/20 text-gold-500">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleEdit(post)}
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(post.slug)}
                        variant="outline"
                        size="sm"
                        className="border-red-500 text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 line-clamp-2">{post.excerpt}</p>
                  <div className="mt-3 text-sm text-gray-500">
                    {post.read_time} min read • {new Date(post.created_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdmin;
